// ============================================================================
// FSA PARTNER MASTER SERVICE (NCP-13)
// ============================================================================

import { get, post, put, del } from '../../client/axiosClient';
import { buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import { fsaPartnerEndpoints, PARTNERS_BASE } from '../../endpoints/fsa/fsaPartner.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../../types';
import type {
  Partner, CreatePartnerRequest, UpdatePartnerRequest, PartnerSearchRequest,
} from '../../types/fsa/partner.types';

/** Transform backend PagedResult envelope to frontend PaginatedResponse. */
function toPaged<T>(raw: any): PaginatedResponse<T> {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 20,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
}

class FsaPartnerService {
  // NOTE: pass the RAW path to buildEndpointWithQuery (it prepends /v1 internally).
  async getAll(params?: PartnerSearchRequest): Promise<PaginatedResponse<Partner>> {
    const endpoint = params ? buildEndpointWithQuery(PARTNERS_BASE, params as any) : fsaPartnerEndpoints.list();
    return toPaged<Partner>((await get<any>(endpoint)).data);
  }
  async getById(id: ID): Promise<ApiResponse<Partner>> {
    return (await get<ApiResponse<Partner>>(fsaPartnerEndpoints.byId(String(id)))).data;
  }
  async create(body: CreatePartnerRequest): Promise<ApiResponse<Partner>> {
    return (await post<ApiResponse<Partner>>(fsaPartnerEndpoints.create(), body)).data;
  }
  async update(id: ID, body: UpdatePartnerRequest): Promise<ApiResponse<Partner>> {
    return (await put<ApiResponse<Partner>>(fsaPartnerEndpoints.update(String(id)), body)).data;
  }
  async remove(id: ID): Promise<void> {
    await del(fsaPartnerEndpoints.remove(String(id)));
  }
}

export const fsaPartnerService = new FsaPartnerService();
